import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = 'Bearer ' + token;
  // Mode cabinet comptable — voir authStore.js::setActingAs. Honoré
  // uniquement par les routers du périmètre comptable côté API (transactions,
  // dépenses, comptabilité, TVA, stats, exports, justificatifs, clients,
  // fournisseurs, charges récurrentes) — sans effet ailleurs.
  const actingAs = localStorage.getItem('acting_as_owner_id');
  if (actingAs) config.headers['X-Acting-As-Owner'] = actingAs;
  return config;
});

let isRefreshing = false;
let refreshQueue = [];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    // 403 UPGRADE_REQUIRED — fire global event so UpgradeModal can intercept
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail || error.response?.data || {};
      const code = typeof detail === 'object' ? detail.code : null;
      if (code === 'UPGRADE_REQUIRED') {
        window.dispatchEvent(new CustomEvent('upgrade_required', {
          detail: {
            required_plan: detail.required_plan || null,
            message: detail.message || 'Cette fonctionnalité nécessite un plan supérieur.',
          },
        }));
      }
      return Promise.reject(error);
    }

    // 422 BILLING_PROFILE_INCOMPLETE — fire global event so BillingProfileModal can intercept
    if (error.response?.status === 422) {
      const detail = error.response?.data?.detail || error.response?.data || {};
      const code = typeof detail === 'object' ? detail.code : null;
      if (code === 'BILLING_PROFILE_INCOMPLETE') {
        window.dispatchEvent(new CustomEvent('billing_profile_incomplete', {
          detail: {
            missing_fields: detail.missing_fields || [],
            message: detail.message || 'Complétez votre profil de facturation avant de continuer.',
          },
        }));
        return Promise.reject(error);
      }
    }

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = 'Bearer ' + token;
        return api(original);
      });
    }
    original._retry = true;
    isRefreshing = true;
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) throw new Error('no refresh token');
      const res = await axios.post(BASE_URL + '/auth/refresh', {}, {
        headers: { Authorization: 'Bearer ' + refreshToken },
      });
      const newToken = res.data.access_token;
      localStorage.setItem('access_token', newToken);
      localStorage.setItem('refresh_token', res.data.refresh_token);
      refreshQueue.forEach((p) => p.resolve(newToken));
      refreshQueue = [];
      original.headers.Authorization = 'Bearer ' + newToken;
      return api(original);
    } catch (e) {
      refreshQueue.forEach((p) => p.reject(e));
      refreshQueue = [];
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(e);
    } finally {
      isRefreshing = false;
    }
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data),
  changePassword: (data) => api.put('/auth/me/password', data),
  registerFCMToken: (token) => api.post('/notifications/register-token', { token }),
  verifyEmail: (token) => api.get(`/auth/verify-email?token=${token}`),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  setDriverMode: (value) => api.patch('/auth/profile/driver-mode', { is_driver_mode: value }),
  trialStatus: () => api.get('/auth/trial-status'),
  logout: () => api.post('/auth/logout'),
};

export const transactionsAPI = {
  list: () => api.get('/transactions/'),
  create: (data) => api.post('/transactions/', data),
  update: (id, data) => api.put(`/transactions/${id}`, data),
  delete: (id) => api.delete('/transactions/' + id),
};

const FORM_HEADERS = { headers: { 'Content-Type': undefined } };

export const receiptsAPI = {
  uploadAndOcr: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/receipts/ocr', fd, FORM_HEADERS);
  },
  save: (file, fields) => {
    const fd = new FormData();
    if (file) fd.append('file', file);
    if (fields.expense_id)   fd.append('expense_id',   fields.expense_id);
    if (fields.supplier)     fd.append('supplier',     fields.supplier);
    if (fields.total_ttc  != null) fd.append('total_ttc',  fields.total_ttc);
    if (fields.total_ht   != null) fd.append('total_ht',   fields.total_ht);
    if (fields.tva_amount != null) fd.append('tva_amount', fields.tva_amount);
    if (fields.tva_rate   != null) fd.append('tva_rate',   fields.tva_rate);
    if (fields.receipt_date)  fd.append('receipt_date', fields.receipt_date);
    return api.post('/receipts/', fd, FORM_HEADERS);
  },
  getAll: () => api.get('/receipts/'),
  getById: (id) => api.get(`/receipts/${id}`),
  delete: (id) => api.delete(`/receipts/${id}`),
  linkExpense: (receiptId, expenseId) => api.patch(`/receipts/${receiptId}`, { expense_id: expenseId }),
};

export const expensesAPI = {
  list: (filters) => {
    if (!filters) return api.get('/expenses/');
    const p = new URLSearchParams();
    if (filters.status)      p.append('status', filters.status);
    if (filters.category)    p.append('category', filters.category);
    if (filters.amount_min != null) p.append('amount_min', filters.amount_min);
    if (filters.amount_max != null) p.append('amount_max', filters.amount_max);
    if (filters.date_from)   p.append('date_from', filters.date_from);
    if (filters.date_to)     p.append('date_to', filters.date_to);
    if (filters.search)      p.append('search', filters.search);
    const qs = p.toString();
    return api.get('/expenses/' + (qs ? '?' + qs : ''));
  },
  getStats: () => api.get('/expenses/stats'),
  create: (data) => api.post('/expenses/', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  updateSupplier: (id, supplierId) => api.patch(`/expenses/${id}/supplier`, { supplier_id: supplierId }),
  delete: (id) => api.delete('/expenses/' + id),
  fuelSummary: () => api.get('/expenses/fuel/summary'),
  fuelExportPDF: (year) => api.get(`/expenses/fuel/export?year=${year}`, { responseType: 'blob' }),
};

export const recurringExpensesAPI = {
  list: () => api.get('/recurring-expenses/'),
  all: () => api.get('/recurring-expenses/all'),
  summary: () => api.get('/recurring-expenses/summary'),
  create: (data) => api.post('/recurring-expenses/', data),
  update: (id, data) => api.put(`/recurring-expenses/${id}`, data),
  delete: (id) => api.delete(`/recurring-expenses/${id}`),
};

export const urssafAPI = {
  projection: () => api.get('/urssaf/projection'),
};

export const electricChargesAPI = {
  list: () => api.get('/electric-charges/'),
  create: (data) => api.post('/electric-charges/', data),
  update: (id, data) => api.put(`/electric-charges/${id}`, data),
  delete: (id) => api.delete(`/electric-charges/${id}`),
  stats: (year) => api.get('/electric-charges/stats' + (year ? `?year=${year}` : '')),
};

export const vatAPI = {
  summary: (from, to) => api.get('/vat/summary?date_from=' + from + '&date_to=' + to),
  plateforme: (from, to) => api.get('/vat/plateforme?date_from=' + from + '&date_to=' + to),
};

export const statsAPI = {
  get: (period, start, end) => {
    let url = `/stats/?period=${period}`;
    if (start) url += `&start=${start}`;
    if (end) url += `&end=${end}`;
    return api.get(url);
  },
};

export const accountingAPI = {
  getSummary:      ()          => api.get('/accounting/summary'),
  getJournal:      (year, month) => {
    let url = `/accounting/journal?year=${year}`;
    if (month) url += `&month=${month}`;
    return api.get(url);
  },
  getGrandLivre:   (year)      => api.get(`/accounting/grand-livre?year=${year}`),
  getBalance:      (year)      => api.get(`/accounting/balance?year=${year}`),
  getCompteResultat: (year)    => api.get(`/accounting/compte-resultat?year=${year}`),
  getBilan:        (year)      => api.get(`/accounting/bilan?year=${year}`),
  exportExcel:     (year)      => api.get(`/accounting/export/excel?year=${year}`, { responseType: 'blob' }),
};

// Accès collaborateur-comptable — voir app/routers/accounting_collaborators.py
export const collaboratorsAPI = {
  invite:      (email)  => api.post('/accounting/collaborators/invite', { email }),
  list:        ()       => api.get('/accounting/collaborators'),
  revoke:      (id)     => api.delete(`/accounting/collaborators/${id}`),
  accept:      (token)  => api.post(`/accounting/collaborators/accept/${token}`),
  myClients:   ()       => api.get('/accounting/collaborators/my-clients'),
};

// Rapprochement bancaire — voir app/routers/bank_reconciliation.py
export const bankAPI = {
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bank/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  list:       (year, month, status) => {
    let url = `/bank/transactions?status=${status || 'all'}`;
    if (year)  url += `&year=${year}`;
    if (month) url += `&month=${month}`;
    return api.get(url);
  },
  summary:      (year) => api.get(`/bank/summary${year ? `?year=${year}` : ''}`),
  candidates:   (id)   => api.get(`/bank/transactions/${id}/candidates`),
  match:        (id, payload) => api.post(`/bank/transactions/${id}/match`, payload),
  unmatch:      (id)   => api.post(`/bank/transactions/${id}/unmatch`),
  remove:       (id)   => api.delete(`/bank/transactions/${id}`),
};

const fetchWithAuth = (url, options = {}) => {
  const token = localStorage.getItem('access_token');
  return fetch(BASE_URL + url, {
    ...options,
    headers: { Authorization: 'Bearer ' + token, ...options.headers },
  });
};

export const uploadsAPI = {
  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetchWithAuth('/uploads/avatar', { method: 'POST', body: formData });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetchWithAuth('/uploads/logo', { method: 'POST', body: formData });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  deleteLogo: async () => {
    const res = await fetchWithAuth('/uploads/logo', { method: 'DELETE' });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  uploadTransactionReceipt: async (transactionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetchWithAuth(`/uploads/transaction/${transactionId}`, { method: 'POST', body: formData });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  uploadExpenseReceipt: async (expenseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetchWithAuth(`/uploads/expense/${expenseId}`, { method: 'POST', body: formData });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  deleteTransactionReceipt: async (transactionId) => {
    const res = await fetchWithAuth(`/uploads/transaction/${transactionId}`, { method: 'DELETE' });
    return res.json();
  },
};

export const passwordAPI = {
  forgot: (email) => api.post('/auth/forgot-password', { email }),
  verify: (token) => api.get(`/auth/reset-password/${token}`),
  reset: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
};

export const billingAPI = {
  getPlans:         ()                     => api.get('/billing/plans'),
  getStatus:        ()                     => api.get('/billing/status'),
  getFounderStatus: ()                     => api.get('/billing/founder-status'),
  createCheckout:   (priceId, seats = 1)   =>
    api.post('/billing/create-checkout', { price_id: priceId, seats }),
  getPortal:        ()                     => api.get('/billing/portal'),
  createPortal:     ()                     => api.post('/billing/create-portal', {}),
};

// Flotte est sur devis (refonte d'offre Solo/Pro/Flotte, 2026-08-30) — pas de
// checkout, juste ce formulaire de contact. Public : fonctionne pour un
// prospect sans compte Balshee (voir app/routers/fleet_leads.py, pas
// d'Authorization requis côté backend).
export const fleetLeadsAPI = {
  create: (companyName, estimatedDrivers, contact, message) =>
    api.post('/fleet-leads', {
      company_name: companyName,
      estimated_drivers: estimatedDrivers,
      contact,
      message: message || undefined,
    }),
};

export const exportsAPI = {
  fec:   (from, to) => api.get('/exports/fec?date_from=' + from + '&date_to=' + to, { responseType: 'blob' }),
  csv:   (from, to) => api.get('/exports/csv?date_from=' + from + '&date_to=' + to, { responseType: 'blob' }),
  excel: (year)     => api.get(`/exports/excel?year=${year}`, { responseType: 'blob' }),
};

export const supportAPI = {
  contact: (sujet, message) => api.post('/support/contact', { sujet, message }),
};

export const inboxAPI = {
  get: () => api.get('/notifications/'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
};

export const adminNotificationsAPI = {
  send: (data) => api.post('/admin/notifications', data),
  list: () => api.get('/admin/notifications'),
  delete: (id) => api.delete(`/admin/notifications/${id}`),
};

export const adminAPI = {
  users: (search = '', plan = '', isVerified = '') => {
    let url = `/admin/users?skip=0&limit=200`;
    if (search) url += `&search=${search}`;
    if (plan) url += `&plan=${plan}`;
    if (isVerified !== '') url += `&is_verified=${isVerified}`;
    return api.get(url);
  },
  metrics: () => api.get('/admin/metrics'),
  stats: () => api.get('/admin/stats'),
  funnel: () => api.get('/admin/commercial/funnel'),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  updatePlan: (userId, plan) => api.patch(`/admin/users/${userId}/plan?plan=${plan}`),
  toggleActive: (userId) => api.patch(`/admin/users/${userId}/toggle-active`),
  toggleBeta: (userId) => api.post(`/admin/users/${userId}/beta`),
  suspend: (userId) => api.post(`/admin/users/${userId}/suspend`),
  verify: (userId) => api.post(`/admin/users/${userId}/verify`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  monitoring: () => api.get('/admin/monitoring'),
  securityLogs: (p = {}) => {
    const q = new URLSearchParams();
    if (p.action)    q.append('action', p.action);
    if (p.user_id)   q.append('user_id', p.user_id);
    if (p.date_from) q.append('date_from', p.date_from);
    if (p.date_to)   q.append('date_to', p.date_to);
    if (p.page)      q.append('page', p.page);
    return api.get('/admin/security-logs?' + q.toString());
  },
  auditLogs: (p = {}) => {
    const q = new URLSearchParams();
    if (p.entity)    q.append('entity', p.entity);
    if (p.user_id)   q.append('user_id', p.user_id);
    if (p.date_from) q.append('date_from', p.date_from);
    if (p.date_to)   q.append('date_to', p.date_to);
    if (p.page)      q.append('page', p.page);
    return api.get('/admin/audit-logs?' + q.toString());
  },
  emailFailed:    ()         => api.get('/admin/invoices/email-failed'),
  resendInvoice:  (id)       => api.post(`/admin/invoices/${id}/resend`),
};

export const invoicesAPI = {
  create: (data) => api.post('/invoices/', data),
  getAll: (status) => api.get('/invoices/' + (status ? `?status=${status}` : '')),
  getById: (id) => api.get(`/invoices/${id}`),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  updateStatus: (id, status, paidDate, paymentMethod) =>
    api.patch(`/invoices/${id}/status`, { status, paid_date: paidDate || null, payment_method: paymentMethod || null }),
  getPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  getStats: (dateFrom, dateTo) => {
    const params = [];
    if (dateFrom) params.push(`date_from=${dateFrom}`);
    if (dateTo)   params.push(`date_to=${dateTo}`);
    return api.get('/invoices/stats' + (params.length ? '?' + params.join('&') : ''));
  },
  convertFromQuote: (quoteId) => api.post(`/quotes/${quoteId}/convert-to-invoice`),
  send: (id) => api.post(`/invoices/${id}/send`),
  share: (id) => api.post(`/invoices/${id}/share`),
};

// Réception d'une facture par QR/lien — pendant "invoicesAPI.share" côté
// émetteur. GET est public (résolution du jeton), POST import est authentifié.
export const invoiceShareAPI = {
  resolve: (token) => api.get(`/invoices/share/${token}`),
  import: (token) => api.post(`/invoices/share/${token}/import`),
};

export const clientsAPI = {
  create: (data) => api.post('/clients/', data),
  getAll: (search) => api.get('/clients/' + (search ? `?search=${encodeURIComponent(search)}` : '')),
  getById: (id) => api.get(`/clients/${id}`),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getHistory: (id) => api.get(`/clients/${id}/history`),
};

// Fiche fournisseur — distincte de clientsAPI (dissocié sur demande
// explicite : un fournisseur de dépense n'a rien à voir avec un client
// facturé). Voir Expense.supplier_id côté backend.
export const suppliersAPI = {
  create: (data) => api.post('/suppliers/', data),
  getAll: (search) => api.get('/suppliers/' + (search ? `?search=${encodeURIComponent(search)}` : '')),
  getById: (id) => api.get(`/suppliers/${id}`),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  getHistory: (id) => api.get(`/suppliers/${id}/history`),
};

export const quotesAPI = {
  create: (data) => api.post('/quotes/', data),
  getAll: (status) => api.get('/quotes/' + (status ? `?status=${status}` : '')),
  getById: (id) => api.get(`/quotes/${id}`),
  update: (id, data) => api.put(`/quotes/${id}`, data),
  delete: (id) => api.delete(`/quotes/${id}`),
  updateStatus: (id, status) => api.patch(`/quotes/${id}/status`, { status }),
  getPdf: (id) => api.get(`/quotes/${id}/pdf`, { responseType: 'blob' }),
  send: (id) => api.post(`/quotes/${id}/send`),
};

export const aiAPI = {
  chat: (data) => api.post('/ai/chat', data),
};

export const tasksAPI = {
  status: (taskId) => api.get(`/tasks/${taskId}`),
};

export const fleetAPI = {
  create: (name) => api.post('/fleet/create', { name }),
  getMyFleet: () => api.get('/fleet/me'),
  listMembers: (fleetId) => api.get(`/fleet/${fleetId}/members`),
  invite: (fleetId, email, role = 'driver') => api.post(`/fleet/${fleetId}/invite`, { email, role }),
  createMember: (fleetId, email, full_name, role = 'driver') =>
    api.post(`/fleet/${fleetId}/members`, { email, full_name, role }),
  acceptInvite: (token) => api.post(`/fleet/accept/${token}`),
  deactivate: (fleetId, userId) => api.patch(`/fleet/${fleetId}/members/${userId}/deactivate`),
  reactivate: (fleetId, userId) => api.patch(`/fleet/${fleetId}/members/${userId}/reactivate`),
  disband: (fleetId) => api.delete(`/fleet/${fleetId}`),
  leave: () => api.post('/fleet/leave'),
};

export const featuresAPI = {
  notify: (feature) => api.post('/features/notify', { feature }),
  getInterests: (feature) => api.get('/admin/feature-interests' + (feature ? `?feature=${feature}` : '')),
  markNotified: (id) => api.patch(`/admin/feature-interests/${id}/notified`),
};

export const facturxAPI = {
  // Emission
  generate: (invoiceId) => api.post(`/invoices/${invoiceId}/facturx/generate`),
  generateAsync: (invoiceId, sendEmail = false) => api.post(`/invoices/${invoiceId}/facturx/generate/async?send_email=${sendEmail}`),
  download: (invoiceId) => api.get(`/invoices/${invoiceId}/facturx/download`, { responseType: 'blob' }),
  send: (invoiceId) => api.post(`/invoices/${invoiceId}/facturx/send`),
  validate: (invoiceId) => api.post(`/invoices/${invoiceId}/facturx/validate`),
  status: (invoiceId) => api.get(`/invoices/${invoiceId}/facturx/status`),
  // Archive
  verifyIntegrity: (invoiceId) => api.get(`/invoices/${invoiceId}/facturx/archive/verify`),
  listArchives: () => api.get('/facturx/archives'),
  auditLog: (invoiceId) => api.get('/facturx/archives/audit' + (invoiceId ? `?invoice_id=${invoiceId}` : '')),
  // Reception
  receive: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/facturx/receive', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  listReceived: (status) => api.get('/facturx/received' + (status ? `?status=${status}` : '')),
  importAsExpense: (receivedId, category = 'fournisseur') => api.post(`/facturx/received/${receivedId}/import?category=${category}`),
  reject: (receivedId) => api.delete(`/facturx/received/${receivedId}`),
  // Parse (standalone)
  parse: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/facturx/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
};

export const b2brouterAPI = {
  list: () => api.get('/b2brouter/factures'),
  create: (data) => api.post('/b2brouter/factures', data),
  status: (b2brouterId) => api.get(`/b2brouter/factures/${b2brouterId}`),
  syncStatus: (invoiceId) => api.post(`/b2brouter/sync/${invoiceId}`),
  account: () => api.get('/b2brouter/account'),
  // peppolMigrationCode : à fournir uniquement quand annuaireStatus() renvoie
  // status "registered_elsewhere" (SIREN déjà actif chez une autre PA) — voir
  // POST /b2brouter/activate côté API.
  activate: (peppolMigrationCode) => api.post('/b2brouter/activate', peppolMigrationCode ? { peppol_migration_code: peppolMigrationCode } : {}),
  // Statut Annuaire en direct (indicateur "chez nous"/"ailleurs"/"pas encore"),
  // distinct de account() qui ne reflète que l'état stocké côté Balshee.
  annuaireStatus: () => api.get('/b2brouter/annuaire-status'),
  // Migration Peppol sortante ("Quitter B2Brouter") — code à transmettre à la
  // nouvelle Plateforme Agréée.
  getMigrationCode: () => api.post('/b2brouter/account/migration-code'),
  cancelMigrationCode: () => api.delete('/b2brouter/account/migration-code'),
  // Factures reçues (fournisseurs) — jamais stockées localement tant que non
  // importées, toujours lues en direct depuis B2Brouter.
  listReceived: () => api.get('/b2brouter/received'),
  importReceived: (providerInvoiceId, category = 'fournisseur') =>
    api.post(`/b2brouter/received/${providerInvoiceId}/import?category=${encodeURIComponent(category)}`),
};

export const companiesAPI = {
  // Recherche officielle par SIRET (lecture seule, ne crée rien)
  lookup: (siret) => api.get(`/companies/lookup?siret=${encodeURIComponent(siret)}`),
  // Génère (ou récupère, idempotent) le lien de partage de mon entreprise
  share: () => api.post('/companies/share'),
  regenerateShare: () => api.post('/companies/share/regenerate'),
  // Résolution publique — pas de token requis, utilisable sans compte
  getSharedCard: (token) => api.get(`/companies/share/${token}`),
  // Réception : importe la fiche pointée par le lien comme client (authed)
  importSharedCard: (token) => api.post(`/companies/share/${token}/import`),
};

export default api;
